import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Renderer2 } from '@angular/core';

const SHADOW_CLASS = 'a-scroll-shadow';
const SCROLLED_CLASS = 'a-scroll-shadow--scrolled';
const MORE_BELOW_CLASS = 'a-scroll-shadow--more-below';

/**
 * Reveals the `.a-scroll-shadow` gradient at the top of a scroll region once it is scrolled down,
 * and marks it `--more-below` while content runs on below its bottom edge (styled per region).
 *
 * Host it on the scroll container OR any ancestor of it — the directive listens for scroll in the
 * capture phase, so whatever element actually scrolls underneath (a native overflow container, or a
 * nested viewport such as ngx-scrollbar's) is discovered at runtime and decorated directly. That
 * self-discovery is deliberate: which element scrolls depends on layout that isn't knowable from the
 * template. Classes are toggled through the renderer, so an OnPush host needs no change detection.
 */
@Directive({
	selector: '[appScrollShadow]',
})
export class ScrollShadowDirective implements AfterViewInit, OnDestroy {
	// WeakSet: only tracks whether a scroller is already decorated, and lets detached scrollers
	// (the navigator swaps components on tab change) be garbage-collected.
	private readonly scrollers = new WeakSet<HTMLElement>();
	private readonly onScroll = (event: Event): void => this.decorate(event.target as HTMLElement | null);
	// A host that scrolls itself is checked whenever its size or its content changes, so a region
	// that overflows shows it before anyone scrolls, and one that fits never does. Whether the host
	// scrolls is asked each time: its styles may not apply yet when the directive starts.
	private resizeObserver?: ResizeObserver;
	private mutationObserver?: MutationObserver;

	constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2, private zone: NgZone) {}

	ngAfterViewInit(): void {
		this.zone.runOutsideAngular(() => {
			// Capture phase so scroll from any descendant scroll container reaches us (scroll doesn't bubble).
			this.el.nativeElement.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
			const host = this.el.nativeElement;
			const decorateIfScroller = (): void => {
				if (['auto', 'scroll'].includes(getComputedStyle(host).overflowY)) {
					this.decorate(host);
				}
			};
			decorateIfScroller();
			this.resizeObserver = new ResizeObserver(decorateIfScroller);
			this.resizeObserver.observe(host);
			this.mutationObserver = new MutationObserver(decorateIfScroller);
			this.mutationObserver.observe(host, { childList: true, subtree: true });
		});
	}

	ngOnDestroy(): void {
		this.el.nativeElement.removeEventListener('scroll', this.onScroll, { capture: true });
		this.resizeObserver?.disconnect();
		this.mutationObserver?.disconnect();
	}

	private decorate(scroller: HTMLElement | null): void {
		if (!scroller || scroller.nodeType !== Node.ELEMENT_NODE) return;

		if (!this.scrollers.has(scroller)) {
			this.scrollers.add(scroller);
			this.renderer.addClass(scroller, SHADOW_CLASS);
		}
		if (scroller.scrollTop > 0) {
			this.renderer.addClass(scroller, SCROLLED_CLASS);
		} else {
			this.renderer.removeClass(scroller, SCROLLED_CLASS);
		}
		if (scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1) {
			this.renderer.addClass(scroller, MORE_BELOW_CLASS);
		} else {
			this.renderer.removeClass(scroller, MORE_BELOW_CLASS);
		}
	}
}
